'use client';
import Link from 'next/link';
import { alpha, Box, Typography, useTheme } from '@mui/material';

import { Avatar } from '@/components';
import { EventMarket } from '@/types';
import { formatProbability } from '@/utils';
import { normalizeMarketQuestion, parseOutcomePrices } from '@/utils/marketUtils';

import { ROUTES } from '@/constants/routes.constants';

interface CandidateRowProps {
  market: EventMarket;
  eventId: string;
  index: number;
}

export function CandidateRow({ market, eventId, index }: CandidateRowProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Extract percentage from outcomePrices
  let percentageStr = '0%';
  let percentageValue = 0;

  const prices = parseOutcomePrices(market.outcomePrices);
  if (prices.length > 0) {
    percentageValue = parseFloat(prices[0]) * 100;
    percentageStr = `${formatProbability(parseFloat(prices[0]), 0)}%`;
  }

  const isLeading = index === 0 && percentageValue > 50;
  const name = normalizeMarketQuestion(market);

  const itemColor = theme.palette.primary.main;

  return (
    <Link href={ROUTES.MARKET_DETAILS(eventId)} className="block text-inherit no-underline">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.5,
          px: 1,
          transition: 'all 0.2s ease',
          '&:hover': {
            '& .progress-container': {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
            },
          },
        }}
      >
        {/* Combined Progress, Avatar, and Name Container */}
        <Box
          className="progress-container"
          sx={{
            flex: 1,
            height: '2.5rem',
            bgcolor: isDark
              ? alpha(theme.palette.common.white, 0.05)
              : alpha(theme.palette.common.black, 0.04),
            borderRadius: '0.375rem',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          {/* Progress Fill */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${percentageValue}%`,
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              transition: 'width 0.5s ease-out',
            }}
          />

          {/* Content Overlaid on Bar */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.5 },
              px: { xs: 1, sm: 1.5 },
              width: '100%',
              minWidth: 0,
            }}
          >
            {/* Percentage Inside the Bar */}
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 700,
                color: 'text.primary',
                minWidth: '2.5rem',
                flexShrink: 0,
              }}
            >
              {percentageStr}
            </Typography>

            <Avatar
              src={market.image}
              alt={name}
              size={24}
              sx={{
                borderRadius: '50%',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                flexShrink: 0,
              }}
            />

            {/* Candidate Name */}
            <Typography
              className="candidate-name"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
              }}
            >
              {name}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Link>
  );
}
